import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pencil, Trash2, FileText } from 'lucide-react';

/**
 * Day 10 — Dynamic Chart Entries Table
 * Renders entries for any template using the dynamic field engine.
 * Uses the backend-computed `summary` string; falls back to first 2 values.
 */

function formatDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return '—'; }
}

function fallbackSummary(entry) {
  const vals = entry.values || {};
  const keys = Object.keys(vals).filter(k => k !== 'notes' && vals[k] !== null && vals[k] !== undefined).slice(0, 2);
  if (!keys.length) return '—';
  return keys.map(k => `${k}: ${typeof vals[k] === 'boolean' ? (vals[k] ? 'Yes' : 'No') : vals[k]}`).join(' · ');
}

export function DynamicChartEntriesTable({ entries, canEdit, onEdit, onArchive }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="py-10 text-center space-y-1" data-testid="dyn-entries-empty">
        <FileText className="w-10 h-10 mx-auto text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">No entries recorded yet.</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="dyn-entries-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b">
            <th className="text-left px-3 py-2">Recorded</th>
            <th className="text-left px-3 py-2">Summary</th>
            <th className="text-left px-3 py-2">Notes</th>
            <th className="text-right px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const summary = e.summary || fallbackSummary(e);
            const notes = (e.values && e.values.notes) || e.notes;
            return (
              <tr key={e.id} className="border-b hover:bg-muted/30" data-testid={`dyn-entry-row-${e.id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(e.entry_date)}</td>
                <td className="px-3 py-2" data-testid={`dyn-entry-summary-${e.id}`}>
                  <span className="font-medium">{summary}</span>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground max-w-[260px] truncate" title={notes || ''}>
                  {notes || <span className="italic">(no notes)</span>}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {canEdit && e.status === 'active' && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-xs mr-1" onClick={() => onEdit(e)} data-testid={`dyn-entry-edit-${e.id}`}>
                        <Pencil className="w-3 h-3 mr-1" />Edit
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onArchive(e)} data-testid={`dyn-entry-archive-${e.id}`}>
                        <Trash2 className="w-3 h-3 mr-1" />Archive
                      </Button>
                    </>
                  )}
                  {e.status === 'archived' && (
                    <Badge variant="outline" className="text-[10px] bg-slate-100 border-slate-300 text-slate-600">Archived</Badge>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default DynamicChartEntriesTable;
